import { gql } from '@apollo/client';

// Shared task field selection.
const TASK_FIELDS = `
  _id
  invoiceId
  newInvoiceId
  stripeCustomerId
  customerEmail
  ownerEmail
  accountId
  deviceType
  products
  qty
  amount
  currency
  status
  paidAt
  provideGate
  coupons { code amount deviceCount used seq productIndex mintedByStaffEmail mintedAt }
  couponIssuedBeforePayment
  chaseDueDate
  dueDate
  netTermsDays
  collectionMethod
  gateOverrideBy
  gateOverrideAt
  gateOverrideReason
  warnedAt
  deactivatedAt
  deactivatedAccountId
  reenableNotifiedAt
  couponsCancelledAt
  enforceError
  closedAt
  closedByStaffEmail
  dismissedAt
  dismissedBy
  dismissedReason
  note
  createdAt
  lastUpdatedDate
`;

// Order Devices queue/ledger. filter.tab = ACTIVE | ALL | UNPAID_COUPONS;
// filter.search matches email / coupon code / stripe id / invoice id.
export const DEVICE_ORDER_TASKS = gql`
  query deviceOrderTasks($filter: DeviceOrderTaskFilterInput, $pagination: PaginationInput) {
    deviceOrderTasks(filter: $filter, pagination: $pagination) {
      items { ${TASK_FIELDS} }
      total
    }
  }
`;

/** Catalog-priced value of a coupon for one device line — what the mint will use. */
export const DEVICE_ORDER_COUPON_QUOTE = gql`
  query DeviceOrderCouponQuote($invoiceId: String!, $productIndex: Int!, $region: String) {
    deviceOrderCouponQuote(invoiceId: $invoiceId, productIndex: $productIndex, region: $region) {
      deviceName
      sku
      quantity
      currency
      invoiceCurrency
      region
      customerCountry
      unitPrice
      deviceAmount
      shippingAmount
      shippingUnitPrice
      totalAmount
      invoicedAmount
    }
  }
`;

export const ORDER_DEVICE_NOTES = gql`
  query orderDeviceNotes($invoiceId: String!) {
    orderDeviceNotes(invoiceId: $invoiceId) {
      _id
      staffEmail
      createdAt
      text
    }
  }
`;

export const ADD_ORDER_DEVICE_NOTE = gql`
  mutation addOrderDeviceNote($invoiceId: String!, $text: String!) {
    addOrderDeviceNote(invoiceId: $invoiceId, text: $text) {
      _id
      staffEmail
      createdAt
      text
    }
  }
`;

/**
 * Can the signed-in staffer approve issuing a coupon on an UNPAID invoice? Config-driven
 * (ORDER_DEVICE_GATE_BYPASS_EMAILS), so it cannot be derived from the caller's role.
 */
export const ORDER_DEVICE_CAN_BYPASS_PAYMENT_GATE = gql`
  query orderDeviceCanBypassPaymentGate {
    orderDeviceCanBypassPaymentGate
  }
`;

// Manual close — paste an externally-created coupon code + mark done.
// `overrideReason` is required by the server when the invoice is not PAID.
// `acceptRedeemed` (ROOT ONLY) records a code Shopify reports as fully redeemed.
export const CLOSE_DEVICE_ORDER_TASK = gql`
  mutation closeDeviceOrderTask($invoiceId: String!, $couponCode: String!, $productIndex: Int, $overrideReason: String, $acceptRedeemed: Boolean) {
    closeDeviceOrderTask(invoiceId: $invoiceId, couponCode: $couponCode, productIndex: $productIndex, overrideReason: $overrideReason, acceptRedeemed: $acceptRedeemed) {
      ${TASK_FIELDS}
    }
  }
`;

/**
 * Switch an account off for an unpaid device order — the "Account inactive" button.
 *
 * The only path that deactivates anyone: the chase escalates to support@ at the deadline rather
 * than acting on a timer. The server enforces the approver allowlist, requires a REDEEMED coupon,
 * and re-checks Stripe before doing anything — so a payment made since the page loaded refuses
 * the shutoff instead of being discovered afterwards.
 */
export const DEACTIVATE_ACCOUNT_FOR_DEVICE_ORDER = gql`
  mutation DeactivateAccountForDeviceOrder($invoiceId: String!, $reason: String!) {
    deactivateAccountForDeviceOrder(invoiceId: $invoiceId, reason: $reason) {
      ${TASK_FIELDS}
    }
  }
`;

/**
 * Take a PAID order out of the Active queue without a coupon (fulfilled by hand before the app).
 * ROOT ONLY; the server also refuses anything not paid. Restore undoes it.
 */
export const DISMISS_DEVICE_ORDER_TASK = gql`
  mutation dismissDeviceOrderTask($invoiceId: String!, $reason: String!) {
    dismissDeviceOrderTask(invoiceId: $invoiceId, reason: $reason) {
      ${TASK_FIELDS}
    }
  }
`;

export const RESTORE_DEVICE_ORDER_TASK = gql`
  mutation restoreDeviceOrderTask($invoiceId: String!) {
    restoreDeviceOrderTask(invoiceId: $invoiceId) {
      ${TASK_FIELDS}
    }
  }
`;

/**
 * Swap the code already issued for one device line for a different Shopify code. ROOT ONLY. The
 * server verifies the new code in Shopify, refuses a redeemed old one, notes the swap on the order
 * and deactivates the old code.
 */
export const REPLACE_DEVICE_ORDER_COUPON = gql`
  mutation replaceDeviceOrderCoupon($invoiceId: String!, $productIndex: Int!, $newCouponCode: String!, $reason: String!) {
    replaceDeviceOrderCoupon(invoiceId: $invoiceId, productIndex: $productIndex, newCouponCode: $newCouponCode, reason: $reason) {
      ${TASK_FIELDS}
    }
  }
`;

// Mint — create the Shopify discount code in-app (attributed to the caller).
// amount defaults to the order's invoice total when omitted.
// `overrideReason` is required by the server when the invoice is not PAID.
export const MINT_DEVICE_ORDER_COUPON = gql`
  mutation mintDeviceOrderCoupon($invoiceId: String!, $productIndex: Int, $amount: Float, $region: String, $overrideReason: String) {
    mintDeviceOrderCoupon(invoiceId: $invoiceId, productIndex: $productIndex, amount: $amount, region: $region, overrideReason: $overrideReason) {
      ${TASK_FIELDS}
    }
  }
`;

/**
 * Run the 60-day chase now and report what it did. ROOT ONLY, and refused in any environment
 * where the chase is not enabled — it can switch off every screen on an account.
 */
export const RUN_DEVICE_ORDER_ENFORCEMENT = gql`
  mutation runDeviceOrderEnforcement {
    runDeviceOrderEnforcement {
      warned
      deactivated
      couponsCancelled
      reasserted
      reenableRequested
    }
  }
`;

// Sync "Code Used" from Shopify (asyncUsageCount) — optionally one invoice.
// Returns the number of tasks whose coupon usage flipped.
export const SYNC_COUPON_USAGE = gql`
  mutation syncCouponUsage($invoiceIds: [String!]) {
    syncCouponUsage(invoiceIds: $invoiceIds)
  }
`;
